import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { isSharepointListApp, ScrollDirection, SearchResults } from '../..';
import { ResourceViewItemComponent } from '../../../results-views/view-item/resource-view-item.component';
import { EventsService, LogService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { WorkspacesService } from 'src/app/bar/services';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { FavoritesService } from 'src/app/bar/services/favorites.service';
import { PeopleCommandBuilder } from '../../../results-views/people/people-command-builder.service';
import { ContextMenuService } from '@shared/components';
import { HubService } from 'src/app/bar/services/hub.service';
import { DownloadsService } from '@shared/services/downloads.service';
import { ResourcesService } from '@shared/services/resources.service';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { CommandBarService } from 'src/app/bar/services/command-bar.service';
import { PopupService } from '@local/ui-infra';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { RouterService } from '@shared/services/router.service';
import { GlobalErrorHandler } from 'src/app/global-error-handler';
import { ResultsService } from 'src/app/bar/services/results.service';
import { FlagsService } from '@shared/services/flags.service';
import { windowSizeObserver } from '@shared/utils';
import { stopEvent } from '@shared/utils/elements-util';
import { PreviewMode } from '../../../preview/model/preview-mode';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEqual } from 'lodash';
import { KeyName, isArrowKey } from '@local/ts-infra';
import { SearchPopUpService } from '../../../special-views/search-popup/search-popup.service';

@UntilDestroy()
@Component({
  selector: 'gallery-group',
  templateUrl: './gallery-group.component.html',
  styleUrls: ['./gallery-group.component.scss'],
})
export class GalleryGroupComponent extends ResourceViewItemComponent implements OnInit {
  private isFocused: boolean;
  private _innerSelectedIndex: number;
  private _items: SearchResults[];

  get innerSelectedIndex(): number {
    return this._innerSelectedIndex;
  }

  set innerSelectedIndex(value: number) {
    this.onUpdateSelectedIndex.emit(value);
    this._innerSelectedIndex = value;
    this.cdr.markForCheck();
  }

  @Input() set items(val: SearchResults[]) {
    if (isEqual(val, this._items)) {
      return;
    }
    this._items = val;
    this.cdr.markForCheck();
  }

  @Input() set componentFocused(val: { focused: boolean; direction: ScrollDirection }) {
    this.isFocused = val.focused;
    if (this.isFocused) {
      if (!this.innerSelectedIndex && this.innerSelectedIndex !== 0) {
        this.innerSelectedIndex = val.direction === 'up' ? this.items?.length - 1 : 0;
      }
    } else {
      this.innerSelectedIndex = null;
    }
    this.cdr.markForCheck();
  }

  @Input() set previewState(val: PreviewMode) {
    this._previewState = val;
    this.changeHeightItem.emit();
  }

  get items(): SearchResults[] {
    return this._items;
  }

  get previewState(): PreviewMode {
    return this._previewState;
  }

  @Output() focusedFinish = new EventEmitter<{ direction: ScrollDirection; event: CustomKeyboardEvent }>();
  @Output() changeHeightItem = new EventEmitter();
  @Output() onUpdateSelectedIndex = new EventEmitter<number>();

  constructor(
    hostRef: ElementRef,
    protected cdr: ChangeDetectorRef,
    logService: LogService,
    protected applicationsService: ApplicationsService,
    contextMenuService: ContextMenuService,
    peopleCommandBuilder: PeopleCommandBuilder,
    hubService: HubService,
    eventsService: EventsService,
    downloadsService: DownloadsService,
    resourcesService: ResourcesService,
    commandsService: CommandsService,
    commandBarService: CommandBarService,
    popupService: PopupService,
    protected keyboardService: KeyboardService,
    routerService: RouterService,
    errorHandler: GlobalErrorHandler,
    resultsService: ResultsService,
    protected flagsService: FlagsService,
    protected favoritesService: FavoritesService,
    protected blobsService: BlobsService,
    protected workspacesService: WorkspacesService,
    protected searchPopUpService: SearchPopUpService
  ) {
    super(
      hostRef,
      cdr,
      logService,
      applicationsService,
      contextMenuService,
      peopleCommandBuilder,
      hubService,
      eventsService,
      downloadsService,
      resourcesService,
      commandsService,
      commandBarService,
      popupService,
      keyboardService,
      routerService,
      errorHandler,
      resultsService,
      flagsService,
      favoritesService,
      blobsService,
      workspacesService,
      searchPopUpService
    );
    this.isContainerItem = true;
  }

  ngOnInit(): void {
    super.ngOnInit();
    windowSizeObserver()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.changeHeightItem.emit();
      });
  }

  updateSelectedIndex(index: number) {
    this.innerSelectedIndex = index;
    this.cdr.markForCheck();
  }

  closeContextMenu() {
    this.hoveredIndex = null;
  }

  handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {
    if (!this.isFocused) return;
    const key = keys[0];

    if (!isArrowKey(key)) {
      return;
    }

    stopEvent(event);
    switch (key) {
      case 'ArrowLeft':
        this.previousItem(event);
        break;
      case 'ArrowRight':
        this.nextItem(event);
        break;
      case 'ArrowDown':
        if (this.previewState === 'side') {
          this.nextItem(event);
        } else {
          this.finishFocused('down', event);
        }
        break;
      case 'ArrowUp':
        if (this.previewState === 'side') {
          this.previousItem(event);
        } else {
          this.finishFocused('up', event);
        }
        break;
    }
  }

  isSharepointListAppItem(item: SearchResults): boolean {
    return isSharepointListApp(item);
  }

  private nextItem(event: CustomKeyboardEvent) {
    this.innerSelectedIndex++;
    this.cdr.markForCheck();
    if (this.innerSelectedIndex >= this.items.length) {
      this.finishFocused('down', event);
    }
  }

  private previousItem(event: CustomKeyboardEvent) {
    this.innerSelectedIndex--;
    this.cdr.markForCheck();
    if (this.innerSelectedIndex < 0) {
      this.finishFocused('up', event);
    }
  }

  private finishFocused(val: ScrollDirection, event: CustomKeyboardEvent) {
    this.focusedFinish.emit({ direction: val, event });
    this.isFocused = false;
    this.innerSelectedIndex = null;
    this.cdr.markForCheck();
  }
}
